/**
 * Dati dell'azienda intestataria dell'installazione, in un unico posto.
 *
 * Perche' esiste questo file: prima la ragione sociale, la P.IVA e la sede
 * erano scritte a mano in sei punti diversi del codice, e gia' divergevano
 * fra loro (la stessa via compariva scritta in due modi diversi in due file).
 * Su documenti fiscali due verita' diverse della stessa sede sono un problema
 * concreto: basta
 * dimenticare una copia e parte un PDF con i dati sbagliati.
 *
 * Da dove arrivano i valori, in ordine:
 *   1. le variabili d'ambiente VITE_* (vedi .env.example), che restano la base
 *      con cui l'app parte anche prima di aver parlato col database;
 *   2. la riga `company_profile` di `app_settings`, scritta dalla pagina
 *      Impostazioni. All'avvio `applyCompanyProfile()` la sovrappone alla base.
 *
 * Perche' due livelli: le variabili d'ambiente si cambiano solo ripubblicando
 * l'app, quindi non sono utilizzabili da chi non tocca il codice. Il database
 * invece e' modificabile dall'amministratore, ma non c'e' ancora quando
 * compaiono le pagine di accesso: servono entrambi.
 *
 * I valori derivati (localita', pie' di pagina, copyright) sono FUNZIONI e non
 * costanti proprio per questo: una costante verrebbe calcolata all'import,
 * cioe' prima che il database abbia risposto, e resterebbe ferma sui valori
 * d'ambiente anche dopo un salvataggio.
 *
 * Questo modulo non importa nulla di proposito: e' usato anche dalle pagine
 * di accesso e dai template email, e un import di `letterheadPdf.ts` (che
 * carica jsPDF) trascinerebbe la libreria PDF nel bundle d'ingresso.
 */

const env = (key: string, fallback = ''): string => {
  const value = import.meta.env?.[key] as string | undefined;
  return value?.trim() || fallback;
};

export interface Branding {
  /** Ragione sociale completa, come va in fattura. */
  ragioneSociale: string;
  /** Nome breve per interfaccia, email e copyright. */
  nomeBreve: string;
  indirizzo: string;
  cap: string;
  citta: string;
  provincia: string;
  partitaIva: string;
  codiceFiscale: string;
  rea: string;
  /** Dicitura estesa del regime fiscale, stampata sui documenti. */
  regimeFiscale: string;
  /** Codice FatturaPA del regime: RF01 = ordinario, RF19 = forfettario. */
  regimeFiscaleCode: string;
  email: string;
  pec: string;
  /** Codice destinatario SDI dell'azienda, per le fatture che riceve. */
  codiceDestinatario: string;
  telefono: string;
  /** Numero WhatsApp in formato internazionale senza segni, per i link wa.me. */
  whatsapp: string;
  sito: string;
  iban: string;
  banca: string;
  /** Aliquota IVA applicata per impostazione predefinita alle nuove righe. */
  aliquotaIvaDefault: number;
}

/** Elenco dei campi, usato per ripulire quello che arriva dal database. */
const CAMPI_TESTO = [
  'ragioneSociale', 'nomeBreve', 'indirizzo', 'cap', 'citta', 'provincia',
  'partitaIva', 'codiceFiscale', 'rea', 'regimeFiscale', 'regimeFiscaleCode',
  'email', 'pec', 'codiceDestinatario', 'telefono', 'whatsapp', 'sito',
  'iban', 'banca'
] as const;

/** Solo cifre: i link wa.me non accettano spazi, '+' o trattini. */
export const soloCifre = (valore: string): string => valore.replace(/\D/g, '');

/**
 * I valori con cui l'app parte: variabili d'ambiente o, dove manca tutto,
 * un default onesto. Restano disponibili per il pulsante "ripristina".
 */
export const BRANDING_DA_ENV: Readonly<Branding> = Object.freeze({
  ragioneSociale: env('VITE_RAGIONE_SOCIALE', 'SLAB S.r.l.'),
  nomeBreve: env('VITE_NOME_BREVE', 'SLAB'),
  indirizzo: env('VITE_INDIRIZZO_VIA'),
  cap: env('VITE_INDIRIZZO_CAP'),
  citta: env('VITE_INDIRIZZO_COMUNE'),
  provincia: env('VITE_INDIRIZZO_PROVINCIA'),
  partitaIva: env('VITE_PARTITA_IVA'),
  codiceFiscale: env('VITE_CODICE_FISCALE'),
  rea: env('VITE_REA'),
  regimeFiscale: env('VITE_REGIME_FISCALE', 'Regime ordinario'),
  regimeFiscaleCode: env('VITE_REGIME_FISCALE_CODE', 'RF01'),
  email: env('VITE_EMAIL'),
  pec: env('VITE_PEC'),
  codiceDestinatario: env('VITE_CODICE_DESTINATARIO'),
  telefono: env('VITE_TELEFONO'),
  whatsapp: soloCifre(env('VITE_WHATSAPP')),
  sito: env('VITE_SITO'),
  iban: env('VITE_IBAN'),
  banca: env('VITE_BANCA'),
  aliquotaIvaDefault: Number(env('VITE_ALIQUOTA_IVA_DEFAULT', '22')) || 22
});

/**
 * I valori in uso adesso. Sessanta punti del codice leggono `BRANDING.campo`
 * mentre disegnano una schermata o compongono un PDF: tenere lo stesso oggetto
 * e aggiornarlo dall'interno evita di dover passare le impostazioni a mano
 * lungo tutta quella catena. E' l'unico punto del progetto in cui un oggetto
 * esportato cambia: succede solo qui sotto, in `applyCompanyProfile`.
 */
export const BRANDING: Branding = { ...BRANDING_DA_ENV };

/**
 * Tiene solo i campi davvero presenti nella riga del database.
 * Una stringa vuota e' una scelta ("questa azienda non ha la PEC") e vince
 * sull'ambiente; un campo assente invece lascia il valore d'ambiente.
 */
export function normalizzaCompanyProfile(grezzo: unknown): Partial<Branding> {
  if (!grezzo || typeof grezzo !== 'object') return {};
  const dati = grezzo as Record<string, unknown>;
  const pulito: Partial<Branding> = {};

  for (const campo of CAMPI_TESTO) {
    const valore = dati[campo];
    if (typeof valore !== 'string') continue;
    pulito[campo] = campo === 'whatsapp' ? soloCifre(valore) : valore.trim();
  }

  const aliquota = Number(dati.aliquotaIvaDefault);
  if (Number.isFinite(aliquota) && aliquota >= 0 && aliquota <= 100) {
    pulito.aliquotaIvaDefault = aliquota;
  }

  return pulito;
}

/**
 * Sovrappone ai valori d'ambiente quelli salvati in Impostazioni.
 * Si riparte sempre da `BRANDING_DA_ENV`, cosi' togliere un campo dal
 * database lo riporta al valore d'ambiente invece di lasciarlo appiccicato.
 */
export function applyCompanyProfile(daDatabase: unknown): void {
  Object.assign(BRANDING, BRANDING_DA_ENV, normalizzaCompanyProfile(daDatabase));
}

/** True quando l'azienda e' in regime forfettario (fatture senza IVA). */
export function isForfettario(): boolean {
  return BRANDING.regimeFiscaleCode === 'RF19';
}

/** Unisce i pezzi non vuoti, cosi' un campo non ancora compilato non lascia " -  - ". */
const join = (parts: Array<string | undefined>, separator: string): string =>
  parts.filter((part) => part && part.trim()).join(separator);

/** "65100 Pescara (PE)" — salta i pezzi mancanti. */
export function brandingLocalita(): string {
  return join(
    [join([BRANDING.cap, BRANDING.citta], ' '), BRANDING.provincia && `(${BRANDING.provincia})`],
    ' '
  );
}

/** "SLAB S.r.l. — Via ..., 65015 Citta (PE)" */
export function brandingIndirizzoCompleto(): string {
  return join(
    [BRANDING.ragioneSociale, join([BRANDING.indirizzo, brandingLocalita()], ', ')],
    ' — '
  );
}

/**
 * Riga unica di piè di pagina per PDF, stampe ed email.
 * E' la sostituta delle sei copie letterali che c'erano prima.
 */
export function brandingFooter(): string {
  return join(
    [
      brandingIndirizzoCompleto(),
      BRANDING.partitaIva && `P.IVA ${BRANDING.partitaIva}`,
      BRANDING.codiceFiscale && `C.F. ${BRANDING.codiceFiscale}`,
      BRANDING.rea && `REA ${BRANDING.rea}`,
      BRANDING.pec && `PEC ${BRANDING.pec}`,
      BRANDING.email,
      BRANDING.telefono && `Tel. ${BRANDING.telefono}`
    ],
    ' — '
  );
}

/** Versione corta per gli spazi stretti (intestazioni tabelle, email). */
export function brandingFooterCompact(): string {
  return join(
    [
      brandingIndirizzoCompleto(),
      BRANDING.partitaIva && `P.IVA ${BRANDING.partitaIva}`,
      BRANDING.email,
      BRANDING.whatsapp && `WhatsApp: ${BRANDING.telefono || BRANDING.whatsapp}`
    ],
    ' - '
  );
}

export function brandingCopyright(): string {
  return `© ${new Date().getFullYear()} ${BRANDING.nomeBreve}. Tutti i diritti riservati.`;
}
